import { useMemo, useState } from "react";

import type { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  Archive,
  Edit3,
  Image as ImageIcon,
  Loader2,
  Megaphone,
  MoreHorizontal,
  Plus,
} from "lucide-react";

import { PopupFormSheet } from "@/features/popups/presentation/components/PopupFormSheet";
import { ConfirmDialog } from "@/shared/components/data/ConfirmDialog";
import { DataTable } from "@/shared/components/data/DataTable";
import { DataToolbar } from "@/shared/components/data/DataToolbar";
import { Pagination } from "@/shared/components/data/Pagination";
import { StatusPill, type StatusTone } from "@/shared/components/data/StatusPill";
import { PageHeader } from "@/shared/components/layout/PageHeader";
import { Button } from "@/shared/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useDebouncedValue } from "@/shared/hooks/use-debounced-value";
import { useQueryState } from "@/shared/hooks/use-query-state";
import { toast } from "@/shared/lib/toast";
import { cn } from "@/shared/lib/utils";
import {
  PopupStatus,
  Stage,
  useArchivePopupMutation,
  useListPopupsQuery,
} from "@/store/apis/adminApi.enhanced";
import type { Popup } from "@/store/apis/adminApi.enhanced";
import type { ListPopupsApiArg } from "@/store/apis/adminApi.generated";

type Sort = NonNullable<ListPopupsApiArg["sort"]>;
type PlatformFilter = NonNullable<ListPopupsApiArg["platform"]>;

const DEFAULT_LIMIT = 25;

const STATUS_TONE: Record<PopupStatus, StatusTone> = {
  [PopupStatus.Live]: "success",
  [PopupStatus.Scheduled]: "info",
  [PopupStatus.Paused]: "warning",
  [PopupStatus.Expired]: "muted",
  [PopupStatus.Archived]: "muted",
};

const STATUS_LABEL: Record<PopupStatus, string> = {
  [PopupStatus.Live]: "En vivo",
  [PopupStatus.Scheduled]: "Programado",
  [PopupStatus.Paused]: "Pausado",
  [PopupStatus.Expired]: "Expirado",
  [PopupStatus.Archived]: "Archivado",
};

const STAGE_LABEL: Record<Stage, string> = {
  [Stage.Trying]: "Trying",
  [Stage.Fertility]: "Fertilidad",
  [Stage.Loss]: "Pérdida",
  [Stage.Lost]: "Lost",
};

export function PopupsPage() {
  const { get, set } = useQueryState();

  const search = get("q", "");
  const debouncedSearch = useDebouncedValue(search, 300);
  const statusFilter = get("status", "");
  const stageFilter = get("stage", "");
  const platformFilter = get("platform", "");
  const sort = (get("sort", "-priority") || "-priority") as Sort;
  const offset = Number(get("offset", "0")) || 0;
  const limit = Number(get("limit", String(DEFAULT_LIMIT))) || DEFAULT_LIMIT;

  const filtersActive =
    debouncedSearch.trim().length > 0 ||
    Boolean(statusFilter) ||
    Boolean(stageFilter) ||
    Boolean(platformFilter);

  const { data, isLoading, isFetching, isError, error, refetch } = useListPopupsQuery({
    q: debouncedSearch || undefined,
    status: (statusFilter as PopupStatus) || undefined,
    stage: (stageFilter as Stage) || undefined,
    platform: (platformFilter as PlatformFilter) || undefined,
    sort,
    offset,
    limit,
  });

  const [archivePopup] = useArchivePopupMutation();

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState<Popup | null>(null);

  const openCreate = () => {
    setEditing(null);
    setSheetOpen(true);
  };
  const openEdit = (p: Popup) => {
    setEditing(p);
    setSheetOpen(true);
  };

  const handleArchive = async (p: Popup) => {
    try {
      await archivePopup({ id: p.id }).unwrap();
      toast.success("Pop-up archivado");
    } catch (err) {
      toast.error(err);
      throw err;
    }
  };

  const columns = useMemo<ColumnDef<Popup>[]>(
    () => [
      {
        id: "title",
        header: "Pop-up",
        cell: ({ row }) => {
          const p = row.original;
          return (
            <div className="flex items-start gap-3">
              {p.image_url ? (
                <img
                  src={p.image_url}
                  alt=""
                  className="h-10 w-10 shrink-0 rounded-lg object-cover ring-1 ring-border"
                />
              ) : (
                <div
                  aria-hidden
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground"
                >
                  <ImageIcon className="h-4 w-4" />
                </div>
              )}
              <div className="min-w-0">
                <p className="truncate text-[13.5px] font-medium text-foreground">
                  {p.title?.trim() || <span className="text-muted-foreground">Sin título</span>}
                </p>
                {p.body ? (
                  <p className="line-clamp-1 text-[12.5px] text-muted-foreground">{p.body}</p>
                ) : null}
              </div>
            </div>
          );
        },
      },
      {
        id: "status",
        header: "Estado",
        cell: ({ row }) => (
          <StatusPill tone={STATUS_TONE[row.original.status]}>
            {STATUS_LABEL[row.original.status]}
          </StatusPill>
        ),
      },
      {
        id: "target",
        header: "Segmento",
        cell: ({ row }) => {
          const stages = row.original.target_stages ?? [];
          if (stages.length === 0) {
            return <span className="text-[12.5px] text-muted-foreground">Todas las etapas</span>;
          }
          return (
            <div className="flex flex-wrap gap-1">
              {stages.map((s) => (
                <span
                  key={s}
                  className="inline-flex rounded border border-border/60 bg-muted/40 px-1.5 py-0.5 font-mono text-[10.5px] text-foreground"
                >
                  {STAGE_LABEL[s]}
                </span>
              ))}
            </div>
          );
        },
      },
      {
        id: "priority",
        header: "Prio.",
        cell: ({ row }) => (
          <span className="font-mono text-[12px] tabular-nums text-foreground">
            {row.original.priority}
          </span>
        ),
      },
      {
        id: "schedule",
        header: "Vigencia",
        cell: ({ row }) => {
          const start = row.original.start_at;
          const end = row.original.end_at;
          if (!start && !end) {
            return <span className="font-mono text-[12px] text-muted-foreground">—</span>;
          }
          return (
            <span className="font-mono text-[11.5px] text-muted-foreground">
              {start ? format(new Date(start), "d MMM", { locale: es }) : "∞"}
              {" → "}
              {end ? format(new Date(end), "d MMM", { locale: es }) : "∞"}
            </span>
          );
        },
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Acciones</span>,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" />
                  <span className="sr-only">Acciones</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => openEdit(row.original)}>
                  <Edit3 className="h-3.5 w-3.5" />
                  Editar
                </DropdownMenuItem>
                {row.original.status !== PopupStatus.Archived ? (
                  <>
                    <DropdownMenuSeparator />
                    <ConfirmDialog
                      trigger={
                        <DropdownMenuItem
                          onSelect={(e) => e.preventDefault()}
                          className="text-destructive focus:text-destructive"
                        >
                          <Archive className="h-3.5 w-3.5" />
                          Archivar
                        </DropdownMenuItem>
                      }
                      title="Archivar pop-up"
                      description={
                        <>
                          El pop-up{" "}
                          <strong className="text-foreground">
                            {row.original.title || row.original.id}
                          </strong>{" "}
                          dejará de mostrarse en la app. Esta acción es reversible desde el back.
                        </>
                      }
                      confirmLabel="Sí, archivar"
                      onConfirm={() => handleArchive(row.original)}
                    />
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <>
      <PageHeader
        kicker="Comunicación"
        title="Pop-ups"
        description="Mensajes que se muestran a la usuaria cuando abre la app, segmentados por etapa, plataforma y cronograma."
        actions={
          <Button size="sm" onClick={openCreate}>
            <Plus className="h-3.5 w-3.5" />
            Nuevo pop-up
          </Button>
        }
      />

      <DataToolbar
        search={search}
        onSearchChange={(next) => set({ q: next, offset: 0 })}
        searchPlaceholder="Buscar por título o texto…"
        filters={
          <>
            <Select
              value={statusFilter || "all"}
              onValueChange={(v) => set({ status: v === "all" ? "" : v, offset: 0 })}
            >
              <SelectTrigger className="h-10 w-[150px] text-[13px]">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                {Object.values(PopupStatus).map((s) => (
                  <SelectItem key={s} value={s}>
                    {STATUS_LABEL[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={stageFilter || "all"}
              onValueChange={(v) => set({ stage: v === "all" ? "" : v, offset: 0 })}
            >
              <SelectTrigger className="h-10 w-[140px] text-[13px]">
                <SelectValue placeholder="Etapa" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las etapas</SelectItem>
                {Object.values(Stage).map((s) => (
                  <SelectItem key={s} value={s}>
                    {STAGE_LABEL[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={platformFilter || "all"}
              onValueChange={(v) => set({ platform: v === "all" ? "" : v, offset: 0 })}
            >
              <SelectTrigger className="h-10 w-[130px] text-[13px]">
                <SelectValue placeholder="Plataforma" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="ios">iOS</SelectItem>
                <SelectItem value="android">Android</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sort ?? "-priority"} onValueChange={(v) => set({ sort: v })}>
              <SelectTrigger className="h-10 w-[180px] text-[13px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="-priority">Prioridad ↓</SelectItem>
                <SelectItem value="priority">Prioridad ↑</SelectItem>
                <SelectItem value="-start_at">Inicio reciente</SelectItem>
                <SelectItem value="start_at">Inicio antiguo</SelectItem>
                <SelectItem value="-updated_at">Actualizado reciente</SelectItem>
                <SelectItem value="-created_at">Creado reciente</SelectItem>
              </SelectContent>
            </Select>
          </>
        }
        actions={
          isFetching && !isLoading ? (
            <span
              className={cn(
                "inline-flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground",
              )}
            >
              <Loader2 className="h-3 w-3 animate-spin" />
              Actualizando
            </span>
          ) : null
        }
      />

      <DataTable
        columns={columns}
        data={data?.items}
        loading={isLoading}
        error={isError ? error : undefined}
        onRetry={() => void refetch()}
        onRowClick={openEdit}
        getRowId={(row) => row.id}
        filtersActive={filtersActive}
        empty={{
          icon: <Megaphone className="h-6 w-6" />,
          title: "Aún no hay pop-ups",
          description: "Crea el primero para empezar a comunicarte con las usuarias.",
          actions: (
            <Button onClick={openCreate}>
              <Plus className="h-3.5 w-3.5" />
              Nuevo pop-up
            </Button>
          ),
        }}
        noResults={{
          actions: (
            <Button
              variant="outline"
              size="sm"
              onClick={() => set({ q: "", status: "", stage: "", platform: "", offset: 0 })}
            >
              Limpiar filtros
            </Button>
          ),
        }}
      />

      <Pagination
        total={data?.total ?? 0}
        limit={limit}
        offset={offset}
        onOffsetChange={(next) => set({ offset: next })}
        onLimitChange={(next) => set({ limit: next })}
      />

      <PopupFormSheet open={sheetOpen} onOpenChange={setSheetOpen} popup={editing} />
    </>
  );
}
